// The card works the deal (module 10 U258; DEAL-WORK-BRIEF-2026-10-04.md section 4, "The card's step box"; REQ-2026-10-05-07,
// the Deal work lane's W4). Mike chose "Triage's flow on the card": the step's options, one yellow lead and up to two more,
// each opening triage's own draft-and-send in place, in the card. What is sent is filed under the deal as its copy comes
// back from the sender's mailbox (migration 203), and nobody comes back to update Cobrain by hand.
//
// Where the deal is in the reader's triage the options are that item's own (the ranker's words), and Send records the
// item's decision as the triage card does. Otherwise they are the deal's own, from its newest message by the option
// engine's rules (apps/api/src/deals/work/options.ts). An option that is not a draft still opens the item, as before.
import { ActionTextarea, ActionButton } from '../parts/index.js';
import { useEffect, useState } from 'react';
import { DraftPanel, openedAsOf, type OpenedAs, type SentFrom } from '../DraftPanel.js';
import { ApiError, type Draft } from '../api.js';
import type { Lead, LeadOption } from '../deals/api.js';
import { dayWords } from '../deals/live-words.js';
import { replyDrafts } from '../reply/api.js';
import { dealWorkApi, DealWorkError, type DealLatest, type DealOption } from './api.js';
import './dealwork.css';

/** What the deal's own options answer, in a line: who wrote last, when, and about what. */
export function latestWords(l: DealLatest, dayStart: string | null): string {
  const day = dayWords(l.at, dayStart);
  const about = l.subject ? `: ${l.subject}` : '';
  if (!l.ours) return `${l.who ?? 'They'} wrote last, ${day}${about}`;
  const to = l.who ? `, to ${l.who}` : '';
  return `${l.mine ? 'You' : 'We'} wrote last, ${day}${to}${about}`;
}

/** The draft an option asks for: its purpose and the message, or the triage item it belongs to. Null for an option that is no draft. */
export function draftOf(o: LeadOption): { purpose: 'reply' | 'chase' | 'note'; signalId: string | null; box: boolean } | null {
  const p = o.params ?? {};
  const purpose = p.purpose;
  if (purpose !== 'reply' && purpose !== 'chase' && purpose !== 'note') return null;
  // A Teams answer is posted in Teams as the person, on the item's own page (D202); the card drafts mail.
  if (p.channel === 'teams') return null;
  return { purpose, signalId: typeof p.signalId === 'string' ? p.signalId : null, box: o.kind === 'reply' || p.voice === true };
}

type Open = { key: string; purpose: 'reply' | 'chase' | 'note'; signalId: string | null; passItemId: string | null; from: SentFrom | null; box: boolean; label: string };

export function DealOptions({ store, dealId, lead, dayStart, onOpenItem, onChanged }: {
  store: string; dealId: string; lead: Lead | null; dayStart: string | null;
  onOpenItem?: (passItemId: string) => void; onChanged?: () => void;
}) {
  const [own, setOwn] = useState<{ latest: DealLatest | null; options: DealOption[] } | null>(null);
  const [asking, setAsking] = useState<Open | null>(null);
  const [words, setWords] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [draft, setDraft] = useState<{ id: string; initial: Draft | null; from: SentFrom | null; opened: OpenedAs | null } | null>(null);
  const [wrong, setWrong] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  // The deal's own options are read only when the triage item gives none to draw.
  const leadOptions = lead?.options ?? [];
  useEffect(() => {
    if (leadOptions.length > 0) return;
    let live = true;
    dealWorkApi.steps(store, dealId).then((r) => { if (live) setOwn(r.steps); }).catch(() => undefined);
    return () => { live = false; };
  }, [store, dealId, leadOptions.length]);

  const make = (o: Open, instruction: string | null) => {
    setDrafting(true); setWrong(null); setSent(false);
    replyDrafts.create({ store, ...(o.passItemId ? { passItemId: o.passItemId } : {}), ...(o.signalId ? { signalId: o.signalId } : {}), purpose: o.purpose, instruction })
      .then(async (r) => {
        // The draft becomes this deal's, so its sent copy is filed here; a draft that cannot be is still sendable.
        await dealWorkApi.pinDraft(store, dealId, r.draft.id).catch(() => undefined);
        // Drawn for the channel the create call names from its first frame, as the item page's is (BUGS.md D198, T64).
        setDraft({ id: r.draft.id, initial: r.draft, from: o.from, opened: openedAsOf(r) });
        setAsking(null); setWords('');
      })
      .catch((e: unknown) => setWrong(e instanceof ApiError || e instanceof DealWorkError || e instanceof Error ? e.message : String(e)))
      .finally(() => setDrafting(false));
  };
  const press = (o: Open) => { if (o.box) { setAsking(o); setWords(''); setWrong(null); } else make(o, null); };

  const shown: Array<{ key: string; label: string; note: string | null; go: () => void }> = leadOptions.length > 0
    ? leadOptions.slice(0, 3).map((o) => {
      const d = draftOf(o);
      const passItemId = lead!.passItemId;
      return {
        key: o.key, label: o.label, note: o.note,
        go: d ? () => press({ key: o.key, purpose: d.purpose, signalId: null, passItemId, from: { passItemId, optionKey: o.key }, box: d.box, label: o.label }) : () => onOpenItem?.(passItemId),
      };
    })
    : (own?.options ?? []).map((o) => ({
      key: o.key, label: o.label, note: o.note,
      go: () => press({ key: o.key, purpose: o.purpose, signalId: o.signalId, passItemId: null, from: null, box: o.box, label: o.label }),
    }));

  if (draft) {
    return (
      <div className="dw-work">
        <DraftPanel store={store} draftId={draft.id} initial={draft.initial} from={draft.from} opened={draft.opened}
          onClose={() => setDraft(null)}
          onSent={() => { setDraft(null); setSent(true); onChanged?.(); }} />
      </div>
    );
  }
  return (
    <div className="dw-work">
      {leadOptions.length === 0 && own?.latest ? <p className="dw-last">{latestWords(own.latest, dayStart)}</p> : null}
      {leadOptions.length === 0 && own && !own.latest ? <p className="dw-last">Nothing is filed under this deal to answer yet. Cobrain drafts from a message once one is here.</p> : null}
      {sent ? <p className="dw-said" role="status">Sent. It is filed under this deal as its copy comes back from your mailbox.</p> : null}
      {shown.length > 0 ? (
        <div className="opts">
          {shown.map((o, i) => (
            <ActionButton type="button" key={o.key} className={i === 0 ? 'opt lead' : 'opt'} disabled={drafting} onClick={o.go}>
              {o.label}{o.note ? <small>{o.note}</small> : null}
            </ActionButton>
          ))}
          {lead ? <ActionButton type="button" className="hx-link m" onClick={() => onOpenItem?.(lead.passItemId)}>Choose another step</ActionButton> : null}
        </div>
      ) : null}
      {asking ? (
        <form className="dw-box" aria-label={asking.label} onSubmit={(e) => { e.preventDefault(); if (words.trim()) make(asking, words.trim()); }}>
          <label className="dw-l" htmlFor={`dw-box-${dealId}`}>Your words, or what Cobrain should say</label>
          <ActionTextarea id={`dw-box-${dealId}`} rows={3} maxLength={2000} value={words} onChange={(e) => setWords(e.target.value)} />
          <div className="dw-acts">
            <ActionButton type="submit" className="dl-opt lead" disabled={drafting || !words.trim()}>Draft it</ActionButton>
            <ActionButton type="button" className="hx-link" onClick={() => setAsking(null)}>Never mind</ActionButton>
          </div>
        </form>
      ) : null}
      {drafting ? <p className="dw-wait" role="status">Cobrain is drafting it in your voice. This takes a few seconds.</p> : null}
      {wrong ? <p className="dl-err" role="alert">{wrong}</p> : null}
    </div>
  );
}
